import { useMemo } from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import type { Dayjs } from 'dayjs';
import type { SettlementStatusData } from '../../../types/pointhomeSettlementStatus.types';
import SettlementStatusTable from './PointhomeSettlementStatusTable';

type Props = {
  data: SettlementStatusData;
  startDate: Dayjs;
  endDate: Dayjs;
  selectedApp: string | null;
  selectedOs: string;
};

const fmtWon = (v: number) => `₩${Math.round(v).toLocaleString('ko-KR')}`;
const fmtCash = (v: number) => `${Math.round(v).toLocaleString('ko-KR')}`;

type SummaryCardProps = { label: string; value: string };

function SummaryCard({ label, value }: SummaryCardProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid #e2e8f0',
        textAlign: 'center',
      }}
    >
      <Typography variant="body2" color="text.secondary" fontWeight={500} mb={0.5}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} color="#0044D2">
        {value}
      </Typography>
    </Paper>
  );
}

export default function PointhomeSettlementStatusDashboard({ data, startDate, endDate, selectedApp, selectedOs }: Props) {
  const summary = useMemo(() => {
    return data.reduce(
      (acc, row) => ({
        totalRevenue: acc.totalRevenue + (row.totalRevenue ?? 0),
        expectedRevenue: acc.expectedRevenue + (row.expectedRevenue ?? 0),
        commission: acc.commission + (row.commission ?? 0),
        expense: acc.expense + (row.expense ?? 0),
        accumulatedCash: acc.accumulatedCash + (row.accumulatedCash ?? 0),
        usedCash: acc.usedCash + (row.usedCash ?? 0),
      }),
      { totalRevenue: 0, expectedRevenue: 0, commission: 0, expense: 0, accumulatedCash: 0, usedCash: 0 }
    );
  }, [data]);

  return (
    <Box>
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <SummaryCard label="총 매출" value={fmtWon(summary.totalRevenue)} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <SummaryCard label="예상 수익" value={fmtWon(summary.expectedRevenue)} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <SummaryCard label="수수료" value={fmtWon(summary.commission)} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <SummaryCard label="비용" value={fmtWon(summary.expense)} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <SummaryCard label="누적 캐시" value={fmtCash(summary.accumulatedCash)} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <SummaryCard label="사용 캐시" value={fmtCash(summary.usedCash)} />
        </Grid>
      </Grid>

      <SettlementStatusTable
        data={data}
        startDate={startDate}
        endDate={endDate}
        selectedApp={selectedApp}
        selectedOs={selectedOs}
      />
    </Box>
  );
}